// `/all-accounts` — every account in the book, for every role (go-live). It replaced Available;
// `/available` (and `/admin/queue` through it) redirects here, hash and all.
//
// The page is the filter bar, the table, and what each role may do with an available account:
//   - The bar (`book/AccountFilterBar.tsx`, the same shape as My Accounts'; rules in
//     `book/allAccountsFilters.ts`), sticky from 768px up: the quick chips (High priority, owner
//     state, Fit and Engagement levels) with live counts; then search, the ☰ Filters dropdown
//     (Sprint, Pool, Strategist, Vertical, `book/AllAccountsFilterControls.tsx`) with a removable
//     chip per active one, the count and Sort by. The filters are kept in the URL (shared links)
//     and in this browser (a reload without a query).
//   - Sort by and the column headers set one sort state, so each shows what the other picked.
//   - The table: the board's frame (`DenseTable`) and its # · Fit · Engmt columns and hovers, far
//     left, behind the board's own `scores_visible` rule. # re-counts over the filtered rows and is
//     the default order.
//   - The row action, from `/api/me`'s capability flags only (`book/allAccountsActions.ts`): Route
//     to… (the approver's existing `/api/accounts/assign`), Request routing (an AE,
//     `/api/accounts/request-route`), or Request (a strategist, `/api/accounts/claim`). A viewer
//     gets none.
//
// What Available also carried stays, below the table: an approver's Requests waiting and Recommended
// routes (the queue, decided there exactly as before), a requester's My requests, and the upload.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AccessDeniedError, assignAccount, claimAccount, fetchApprovalQueue, fetchBook,
  fetchMyRequests, fetchStrategists, panelMessage, requestRoute,
} from '../api/client';
import type {
  ApprovalQueuePayload, BookAccount, BookPayload, EnrichCandidate,
  MyRequestsPayload, WriteActionResult,
} from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { SectionHead } from '../components/SectionHead';
import { CsvIntake } from '../book/CsvIntake';
import { AvailableTable } from '../book/AvailableTable';
import { BankHealthStrip, QueueOutcome, useQueueWrite } from '../book/ApprovalQueue';
import { AllAccountsActionForm } from '../book/AllAccountsActionForm';
import type { ActionSubmission } from '../book/AllAccountsActionForm';
import { AccountFilterBar, SortBySelect } from '../book/AccountFilterBar';
import { AllAccountsFilterControls } from '../book/AllAccountsFilterControls';
import { poolChoices, sprintChoices, sprintKeySet } from '../book/sprints';
import { allAccountsColumns } from '../book/allAccountsColumns';
import { withPriorityColumn } from '../book/PriorityFlagButton';
import { usePriorityToggle } from '../book/usePriorityToggle';
import type { AllAccountsRow } from '../book/allAccountsColumns';
import { actionKindFor, actionVerb, rowActionFor } from '../book/allAccountsActions';
import type { RowActionKind } from '../book/allAccountsActions';
import {
  FILTERS_STORAGE_KEY, activeFilterCount, applyFilters, clearAllFilters, filterCounts, initialFilters, ownerOptions,
  ownerStateOf, paramsWithFilters, storedFromFilters, strategistEmailSet, verticalOptions, withSprintFromPool,
} from '../book/allAccountsFilters';
import type { AllAccountsFilters, OwnerContext, OwnerState } from '../book/allAccountsFilters';
import { bankEmailsOf, bankRows } from '../book/bank';
import { applyAssignToBook, applyClaimToBook, applyClaimToMine, requestForAccount } from '../book/bankRequest';
import { bandRows, sortBands } from '../book/bands';
import { DenseTable } from '../book/DenseTable';
import { attachRanking } from '../book/derived';
import { MyRequests } from '../book/MyRequests';
import { strategistLabel } from '../book/queueSignal';
import { attachScores, rankByScore } from '../book/scores';
import { RANK_ORDER, WORKLIST_ORDER, cycleSort, sortRows } from '../book/sort';
import type { SortState } from '../book/sort';
import { useScores } from '../book/useScores';
import { formatCount } from '../lib/format';
import { offBoardPanel } from '../lib/roleViews.ts';
import { viewAsWritesAllowed } from '../lib/viewAs';
import { writeOutcomeLine, writeOutcomeTone } from '../lib/writeOutcome.ts';
import { applyEnrichedPerson } from '../lib/enrichedBook';
import { Sidebar } from '../sidebar/Sidebar';

// One divider over the table, as the board draws its sections.
const GROUP_ALL = 0;

// Guarded: private mode or a blocked API must not take the page with it.
function readStoredFilters(): string | null {
  try {
    return window.localStorage.getItem(FILTERS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredFilters(filters: AllAccountsFilters): void {
  try {
    window.localStorage.setItem(FILTERS_STORAGE_KEY, storedFromFilters(filters));
  } catch {
    // Storage is unavailable; the URL still carries the filters for this visit.
  }
}

// Who owns a row, by name, for the Owner column and "Owned by …".
function ownerNameOf(row: BookAccount, state: OwnerState): string {
  if (state === 'strategist' || state === 'sdr') return row.sdr_owner || row.sdr_owner_email || '';
  if (state === 'ae') return row.account_owner || row.account_owner_email || '';
  return '';
}

interface OpenAction {
  account_id: string;
  kind: RowActionKind;
}

export function AllAccountsView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openAccountId, setOpenAccountId] = useState<string | null>(searchParams.get('account'));
  const [openAction, setOpenAction] = useState<OpenAction | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [result, setResult] = useState<WriteActionResult | null>(null);
  const [resultVerb, setResultVerb] = useState('Request');
  const [error, setError] = useState<string | null>(null);
  const [chosenSort, setChosenSort] = useState<SortState | null>(null);
  const [filters, setFilters] = useState<AllAccountsFilters>(() => initialFilters(searchParams, readStoredFilters()));
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // The High priority flag's toggle (optimistic, rolled back on a failed write).
  const priority = usePriorityToggle(getToken);

  const bookQuery = useQuery({
    queryKey: ['book'],
    queryFn: () => fetchBook(getToken),
  });
  const book = bookQuery.data;
  const viewer = book?.viewer;
  const seesQueue = Boolean(viewer?.sees_queue);
  const actionKind = actionKindFor(viewer);
  const canRequest = actionKind === 'request' || actionKind === 'request_route';

  const mineQuery = useQuery({
    queryKey: ['my-requests'],
    queryFn: () => fetchMyRequests(getToken),
    enabled: canRequest,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const refresh = useMutation({
    mutationFn: () => fetchBook(getToken, true),
    onSuccess: payload => queryClient.setQueryData<BookPayload>(['book'], payload),
  });

  const queueQuery = useQuery({
    queryKey: ['approval-queue'],
    queryFn: () => fetchApprovalQueue(getToken),
    enabled: seesQueue,
  });

  // The listed strategists: who counts as Strategist-owned, and the route pickers' list. Every role
  // that reads the book may read it.
  const strategistsQuery = useQuery({
    queryKey: ['strategists'],
    queryFn: () => fetchStrategists(getToken),
    enabled: Boolean(book),
  });
  const strategists = useMemo(() => strategistsQuery.data?.strategists ?? [], [strategistsQuery.data]);

  // The server says which way the upload panel writes, or that there is no panel.
  const intakeMode = viewer?.sees_csv_intake ? (viewer.intake_mode ?? null) : null;

  const today = useMemo(
    () => new Date((book?.today ?? '1970-01-01') + 'T00:00:00Z'),
    [book?.today],
  );
  const scores = useScores(getToken, viewer);

  // The bank's address plus its aliases, one list for Available, Mine, the owner options and the bank.
  const bankEmails = useMemo(() => bankEmailsOf(book), [book?.bank_owner_email, book?.bank_owner_emails]);

  // Owner state is decided against the viewer, the bank and the strategist list. Until the list
  // answers (or if it fails), the book's own owner addresses stand in, so nothing reads SDR-owned.
  const ownerContext = useMemo<OwnerContext>(() => ({
    viewer_email: viewer?.email ?? '',
    bank_owner_email: bankEmails,
    strategist_emails: strategistEmailSet(
      strategistsQuery.data ? strategists.map(row => row.email) : (book?.owner_emails ?? []),
    ),
    scores_shown: scores.shown,
  }), [viewer?.email, bankEmails, book?.owner_emails, strategistsQuery.data, strategists, scores.shown]);

  // Every account with its scores, before any filter.
  const scoredAccounts = useMemo(
    () => attachScores(book?.accounts ?? [], scores.payload),
    [book?.accounts, scores.payload],
  );
  const filtered = useMemo(
    () => applyFilters(scoredAccounts, filters, ownerContext),
    [scoredAccounts, filters, ownerContext],
  );
  const counts = useMemo(
    () => filterCounts(scoredAccounts, filters, ownerContext),
    [scoredAccounts, filters, ownerContext],
  );
  const verticals = useMemo(() => verticalOptions(scoredAccounts), [scoredAccounts]);
  const owners = useMemo(() => ownerOptions(scoredAccounts, bankEmails), [scoredAccounts, bankEmails]);
  // The sprints and the other checked-in pools on the book (`sprints.ts`, as on My Accounts); each
  // dropdown, and the Sprint / pool column, hides without one.
  const sprints = useMemo(() => sprintChoices(scoredAccounts), [scoredAccounts]);
  const pools = useMemo(() => poolChoices(scoredAccounts), [scoredAccounts]);
  const poolsShown = sprints.length > 0 || pools.length > 0;

  // The rows the table shows: the board's ranking join, # re-counted over the filtered rows, and
  // this page's owner state and action.
  const tableRows = useMemo<AllAccountsRow[]>(() => {
    if (!book) return [];
    const scoreRanks = rankByScore(filtered);
    const nameOf = (address: string) => strategistLabel(address, strategists);
    return attachRanking(filtered, null, book.generated_at, book.today).map(row => {
      const ownerState = ownerStateOf(row, ownerContext);
      const ownerName = ownerNameOf(row, ownerState);
      const rowAction = rowActionFor(viewer, {
        row, owner_state: ownerState, owner_name: ownerName, request: requestForAccount(book, row.id),
        viewer_email: viewer?.email ?? '', name_of: nameOf,
      });
      return {
        ...row,
        score_rank: scoreRanks.get(row.id) ?? null,
        owner_state: ownerState,
        owner_name: ownerName,
        row_action: rowAction,
        action_busy: busyId === row.id,
        board_pref: null,
        group: GROUP_ALL,
      };
    });
  }, [book, filtered, strategists, ownerContext, viewer, busyId]);

  // The action button opens the form for its row. Stable, so the columns (and every memoised
  // row) are not rebuilt on each render.
  const actionKindRef = useRef(actionKind);
  actionKindRef.current = actionKind;
  const onAct = useCallback((accountId: string) => {
    const kind = actionKindRef.current;
    if (!kind) return;
    setOpenAction({ account_id: accountId, kind });
    setResult(null);
    setError(null);
  }, []);

  const columns = useMemo(
    () => withPriorityColumn(allAccountsColumns(scores.shown, poolsShown, actionKind !== null, onAct)),
    [scores.shown, poolsShown, actionKind, onAct],
  );

  // # is the default order once scores show; without them, the book's own worklist order.
  const defaultSort = scores.shown ? RANK_ORDER : WORKLIST_ORDER;
  const sort = chosenSort ?? defaultSort;
  const sortedRows = useMemo(() => sortRows(tableRows, sort, columns) as AllAccountsRow[], [tableRows, sort, columns]);

  // The approver's queue bands. Picks take their account from the bank; the rest of the bank is
  // the table above, so its band is left empty here.
  const queueBands = useMemo(() => {
    const picks = seesQueue ? (queueQuery.data?.picks ?? []) : [];
    const bank = bankRows(scoredAccounts, bankEmails);
    const split = bandRows(bank, picks);
    return sortBands({ ...split, bank: [] }, WORKLIST_ORDER, today);
  }, [scoredAccounts, bankEmails, queueQuery.data, seesQueue, today]);
  const waitingCount = queueBands.requests.length + queueBands.recommended.length;

  const queueWrite = useQueueWrite(next => {
    queryClient.setQueryData<ApprovalQueuePayload>(['approval-queue'], next);
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    const written = (next.results || []).filter(row => row.written);
    if (written.length === 0) return;
    // Stamp the cached book so the row leaves Available immediately, then refetch so the
    // server overlay (and the strategist's book) agrees.
    queryClient.setQueryData<BookPayload>(['book'], current => {
      if (!current) return current;
      return written.reduce((payload, answer) => applyAssignToBook(payload, answer), current);
    });
    void queryClient.invalidateQueries({ queryKey: ['book'] });
    void queryClient.invalidateQueries({ queryKey: ['my-requests'] });
  });

  // An old `/available#proposals` link lands on the queue once it has rendered.
  const queueReady = !seesQueue || !queueQuery.isPending;
  useEffect(() => {
    if (!queueReady) return;
    const id = window.location.hash.replace(/^#/, '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [queueReady]);

  // Filters restored from this browser go onto the URL too, so the address always says what is shown.
  useEffect(() => {
    setSearchParams(current => paramsWithFilters(current, filters), { replace: true });
    // Once, on arrival: every later change writes the URL itself.
  }, []);

  function changeFilters(next: AllAccountsFilters) {
    setFilters(next);
    writeStoredFilters(next);
    setSearchParams(current => paramsWithFilters(current, next), { replace: true });
  }

  // A link or saved copy from before Sprint and Pool were split names a sprint as `?pool=`: once the
  // book says which keys are sprints, move it to `?sprint=` (URL and saved copy), so it still shows.
  useEffect(() => {
    if (!scoredAccounts.length) return;
    const migrated = withSprintFromPool(filters, sprintKeySet(scoredAccounts));
    if (migrated !== filters) changeFilters(migrated);
  }, [scoredAccounts]);

  // The board's cell context: today, the staleness window, and the High priority toggle.
  const cellContext = useMemo(
    () => ({
      today, staleDays: book?.stale_days ?? 14, fitCeiling: book?.fit_score_ceiling,
      priority: { viewer, pendingId: priority.pendingId, onToggle: priority.toggle },
    }),
    [today, book?.stale_days, book?.fit_score_ceiling, viewer, priority.pendingId, priority.toggle],
  );

  if (bookQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={bookQuery.error.message} email={email} onSignOut={signOut} />;
  }

  // A request (for oneself or for a strategist) marks the row Requested from the reply, without
  // a refetch: claim and request-route write no Salesforce field.
  function applyRequestAnswer(answer: WriteActionResult) {
    queryClient.setQueryData<BookPayload>(['book'], current => (
      current ? applyClaimToBook(current, answer, current.viewer.email) : current
    ));
    queryClient.setQueryData<MyRequestsPayload>(['my-requests'], current => (
      applyClaimToMine(current, answer, book?.viewer.email || email || '')
    ));
    void queryClient.invalidateQueries({ queryKey: ['my-requests'] });
    if (seesQueue && answer.outcome === 'requested') {
      void queryClient.invalidateQueries({ queryKey: ['approval-queue'] });
    }
  }

  async function sendAction(account: BookAccount, kind: RowActionKind, submission: ActionSubmission) {
    if (!account.domain) return;
    setBusyId(account.id);
    setError(null);
    setResult(null);
    setResultVerb(actionVerb(kind));
    try {
      // Route to…: the approver's assign, the same writer as Route on Available always was.
      if (kind === 'route') {
        const answer = await assignAccount(getToken, { domain: account.domain, owner: submission.owner, account_id: account.id });
        setResult(answer);
        queryClient.setQueryData<BookPayload>(['book'], current => (current ? applyAssignToBook(current, answer) : current));
      } else if (kind === 'request_route') {
        const answer = await requestRoute(getToken, { account_id: account.id, owner: submission.owner, reason: submission.reason });
        setResult(answer);
        applyRequestAnswer(answer);
      } else {
        const answer = await claimAccount(getToken, { domain: account.domain, account_id: account.id, reason: submission.reason });
        setResult(answer);
        applyRequestAnswer(answer);
      }
      setOpenAction(null);
    } catch (caught) {
      setError(panelMessage(caught, `${actionVerb(kind)} failed.`));
    } finally {
      setBusyId(null);
    }
  }

  function applyEnrichedContact(contactId: string, candidate: EnrichCandidate) {
    queryClient.setQueryData<BookPayload>(['book'], current => current && applyEnrichedPerson(current, contactId, candidate));
  }

  const openAccount = openAccountId ? (book?.accounts.find(row => row.id === openAccountId) ?? null) : null;
  const actionAccount = openAction ? (book?.accounts.find(row => row.id === openAction.account_id) ?? null) : null;

  const body = () => {
    // Held for the scores too, so the first table drawn is already in its final order (GRO-491).
    if (bookQuery.isPending || scores.waiting) return <div className="loading">Loading accounts…</div>;
    if (bookQuery.error) {
      return (
        <div className="err">
          <b>Could not load the book.</b>
          <br />
          {(bookQuery.error as Error).message}
        </div>
      );
    }
    if (!book) return null;
    const activeCount = activeFilterCount(filters, scores.shown);

    return (
      <>
        <SectionHead
          title="All accounts"
          count={`${formatCount(book.accounts.length)} in the book · ${formatCount(counts.owner_states.available)} available`}
        />

        {/* The bar, the open action and its answer stay on screen: `.filterbartop` is sticky (all-accounts.css). */}
        <div className="filterbartop">
          <AccountFilterBar
            ownerChips={{
              selected: filters.owner_states,
              counts: counts.owner_states,
              onChange: next => changeFilters({ ...filters, owner_states: next }),
            }}
            highPriority={{
              pressed: filters.high_priority,
              count: counts.high_priority,
              onToggle: () => changeFilters({ ...filters, high_priority: !filters.high_priority }),
            }}
            scoreLevels={filters}
            scoreCounts={counts}
            scoresShown={scores.shown}
            onScoreLevelsChange={next => changeFilters({ ...filters, ...next })}
          >
            <input className="aasearch" type="search" placeholder="Search account or domain…" aria-label="Search accounts"
              value={filters.search} onChange={event => changeFilters({ ...filters, search: event.target.value })} />
            <AllAccountsFilterControls
              filters={filters}
              onChange={changeFilters}
              isOpen={filterPanelOpen}
              onOpenChange={setFilterPanelOpen}
              sprints={sprints}
              pools={pools}
              owners={owners}
              verticals={verticals}
            />
            <span className="aashown" aria-live="polite">
              {formatCount(sortedRows.length)} of {formatCount(book.accounts.length)} accounts
            </span>
            <SortBySelect sort={sort} scoresShown={scores.shown} columns={columns} onChange={setChosenSort} />
          </AccountFilterBar>
          {openAction && actionAccount ? (
            <AllAccountsActionForm
              key={openAction.account_id}
              accountName={actionAccount.account || actionAccount.domain || ''}
              kind={openAction.kind}
              strategists={strategists}
              strategistsLoading={strategistsQuery.isLoading}
              busy={busyId === openAction.account_id}
              onSend={submission => void sendAction(actionAccount, openAction.kind, submission)}
              onCancel={() => setOpenAction(null)}
            />
          ) : null}
          {result ? <p className={'actionnote ' + writeOutcomeTone(result)}>{writeOutcomeLine(result, resultVerb)}</p> : null}
          {error ? <p className="actionnote bad">{error}</p> : null}
          {priority.note ? (
            <p className="actionnote bad" role="status">
              {priority.note}{' '}
              <button type="button" className="striplink" onClick={priority.dismissNote}>Dismiss</button>
            </p>
          ) : null}
          {seesQueue && waitingCount > 0 ? (
            <p className="aawaiting"><a href="#proposals">{formatCount(waitingCount)} waiting for a decision ↓</a></p>
          ) : null}
        </div>

        {sortedRows.length === 0 ? (
          <p className="briefempty">
            No account matches these filters.{' '}
            {activeCount ? (
              <button type="button" className="striplink" onClick={() => changeFilters(clearAllFilters())}>
                Clear all {activeCount} to see every account.
              </button>
            ) : null}
          </p>
        ) : (
          <div className="boardv2 aatablearea">
            <DenseTable
              rows={sortedRows}
              columns={columns}
              context={cellContext}
              sort={sort}
              onSort={key => setChosenSort(cycleSort(sort, key, defaultSort))}
              openAccountId={openAccountId}
              onToggleAccount={accountId => setOpenAccountId(current => (current === accountId ? null : accountId))}
              onSelectAccount={accountId => setOpenAccountId(accountId)}
              canEditPrefs={false}
              pendingPrefId={null}
              onSetPref={() => {}}
              groupLabels={{ [GROUP_ALL]: 'Accounts' }}
              showRowMenu={false}
              tableClassName="allaccountstable"
            />
          </div>
        )}

        {/* The approver's queue: requests and morning proposals, decided exactly as on Available. */}
        {seesQueue ? (
          <section className="aaqueue">
            <SectionHead title="Requests waiting" count={formatCount(waitingCount)} />
            {queueQuery.data ? (
              <>
                <BankHealthStrip line={queueQuery.data.bank_health.line} />
                {queueQuery.data.mirror_lag ? <p className="qmirror">{queueQuery.data.mirror_lag}</p> : null}
              </>
            ) : null}
            {queueQuery.error ? (
              <div className="err">
                <b>Could not load the queue.</b>
                <br />
                {(queueQuery.error as Error).message}
              </div>
            ) : null}
            {queueQuery.isPending ? <p className="adminloading">Reading the queue…</p> : null}
            {queueReady && waitingCount === 0 && !queueQuery.error ? (
              <p className="briefempty" id="proposals">Nothing is waiting for a decision.</p>
            ) : null}
            {queueReady && waitingCount > 0 ? (
              <AvailableTable
                bands={queueBands}
                sort={WORKLIST_ORDER}
                onSort={() => {}}
                queue
                scoresShown={scores.shown}
                today={today}
                strategists={queueQuery.data?.strategists ?? []}
                canApprove={Boolean(viewer?.can_approve)}
                canClaim={() => false}
                requestFor={accountId => requestForAccount(book, accountId)}
                viewerEmail={book.viewer.email}
                claimingId={null}
                queueBusy={queueWrite.busy}
                onApprove={queueWrite.approve}
                onDecline={queueWrite.decline}
                onClaim={() => {}}
                onRoute={() => {}}
                onOpen={setOpenAccountId}
              />
            ) : null}
            <QueueOutcome declined={queueQuery.data?.declined ?? []} results={queueWrite.results} error={queueWrite.error} />
          </section>
        ) : null}

        {/* A requester's own asks: pending, declined, approved. */}
        {canRequest && !seesQueue ? (
          <MyRequests
            requests={mineQuery.data?.requests ?? []}
            book={book}
            viewerEmail={book.viewer.email}
            today={today}
            onOpen={setOpenAccountId}
          />
        ) : null}

        {/* One panel, two modes, from the server's intake_mode: an approver assigns, a
            strategist's rows become requests. */}
        {intakeMode ? (
          <CsvIntake
            key={intakeMode}
            getToken={getToken}
            mode={intakeMode}
            viewerEmail={book.viewer.email}
            accounts={book.accounts}
            strategists={strategists}
            strategistsLoading={strategistsQuery.isLoading}
            viewAsRole={viewer?.acting_for && !viewAsWritesAllowed(viewer) ? (viewer.role ?? 'viewer') : null}
            onOwnership={answer => {
              queryClient.setQueryData<BookPayload>(['book'], current => {
                if (!current) return current;
                // A request marks the bank row Requested; an assignment moves the owner.
                if (answer.outcome === 'requested') return applyClaimToBook(current, answer, current.viewer.email);
                return applyAssignToBook(current, answer);
              });
              if (answer.outcome === 'requested' || answer.outcome === 'refused') {
                queryClient.setQueryData<MyRequestsPayload>(['my-requests'], current => (
                  applyClaimToMine(current, answer, book.viewer.email)
                ));
                void queryClient.invalidateQueries({ queryKey: ['my-requests'] });
              }
            }}
          />
        ) : null}
      </>
    );
  };

  return (
    <>
      <PageChrome
        freshness={book?.freshness ?? null}
        build={book?.build}
        viewer={book?.viewer}
        isRebuilding={refresh.isPending}
        onRebuild={() => refresh.mutate()}
      >
        {body()}
      </PageChrome>
      <Sidebar
        account={openAccount}
        book={book ?? null}
        onClose={() => setOpenAccountId(null)}
        onContactEnriched={applyEnrichedContact}
        onBookChanged={() => {
          void queryClient.invalidateQueries({ queryKey: ['book'] });
          void queryClient.invalidateQueries({ queryKey: ['my-requests'] });
        }}
        page={offBoardPanel(viewer?.role)}
      />
    </>
  );
}
